import { createSlice } from '@reduxjs/toolkit'

import { readPreferences } from './cardVisibilityStorage'
import { CardVisibilityState, createCardVisibilityReducers, createCardVisibilitySelectors } from './createCardVisibilitySlice'

// Card attributes the board-v2 CustomizeCardsMenu can toggle. This is the
// `supported: true` subset of board-view-2/cardAttributes.ts — keep the two
// in sync. Independent from persistentMasterRouteSlice so the board-v2 and
// master-route boards can diverge.
export const BOARD_V2_CARD_TOGGLE_KEYS = [
  'locationName',
  'serviceAddress',
  'billingAddress',
  'workOrder',
  'serviceName',
  'recurrence',
  'materials',
  'businessLine',
  'action',
  'measure',
  'containerCount',
  'tags',
  'exceptions',
  'notes',
  'purchaseOrder',
  'disposalTicket',
  'images',
  'video',
  'overflowMenu',
] as const

export type BoardV2CardToggleKey = typeof BOARD_V2_CARD_TOGGLE_KEYS[number]

export type PersistentBoardV2CardState = CardVisibilityState<BoardV2CardToggleKey>

// Everything on except billing address, materials, business line and containers —
// those four are the only ones that would add a row of their own. The work-order
// number shares the service-name row and every badge shares the badge row, so
// defaulting them on costs no height (see stopCardHeight.ts).
const initialState: PersistentBoardV2CardState = {
  withLocationName: true,
  withServiceAddress: true,
  withBillingAddress: false,
  withWorkOrder: true,
  withServiceName: true,
  withRecurrence: true,
  withMaterials: false,
  withBusinessLine: false,
  withAction: true,
  withMeasure: true,
  withContainerCount: false,
  withTags: true,
  withExceptions: true,
  withNotes: true,
  withPurchaseOrder: true,
  withDisposalTicket: true,
  withImages: true,
  withVideo: true,
  withOverflowMenu: true,
}

export const BOARD_V2_CARD_STORAGE_KEY = 'hh.routeManager.boardV2Cards'

export const readStoredBoardV2Cards = (): PersistentBoardV2CardState =>
  readPreferences(BOARD_V2_CARD_STORAGE_KEY, 'persistentBoardV2Card', initialState)

const persistentBoardV2CardSlice = createSlice({
  name: 'persistentBoardV2Card',
  initialState: { ...initialState },
  reducers: {
    ...createCardVisibilityReducers<PersistentBoardV2CardState, BoardV2CardToggleKey>(BOARD_V2_CARD_TOGGLE_KEYS),
  },
})

export const { setCardAttributeVisible, setAllCardAttributesVisible, hydrateFromStorage, restoreFromStorage } =
  persistentBoardV2CardSlice.actions

// Narrow root-state shape so selectors and tests don't need the full RootState.
export interface PersistentBoardV2CardRootStateSlice {
  persistentBoardV2Card?: PersistentBoardV2CardState
}

const { selectWith, selectCardAttributeVisibility: selectVisibility } = createCardVisibilitySelectors<BoardV2CardToggleKey>({
  statePath: 'persistentBoardV2Card',
  toggleKeys: BOARD_V2_CARD_TOGGLE_KEYS,
  defaults: initialState,
})

type Selector<T> = (state: PersistentBoardV2CardRootStateSlice) => T

export const selectWithLocationName: Selector<boolean> = selectWith('locationName')
export const selectWithServiceAddress: Selector<boolean> = selectWith('serviceAddress')
export const selectWithBillingAddress: Selector<boolean> = selectWith('billingAddress')
export const selectWithWorkOrder: Selector<boolean> = selectWith('workOrder')
export const selectWithServiceName: Selector<boolean> = selectWith('serviceName')
export const selectWithRecurrence: Selector<boolean> = selectWith('recurrence')
export const selectWithMaterials: Selector<boolean> = selectWith('materials')
export const selectWithBusinessLine: Selector<boolean> = selectWith('businessLine')
export const selectWithAction: Selector<boolean> = selectWith('action')
export const selectWithMeasure: Selector<boolean> = selectWith('measure')
export const selectWithContainerCount: Selector<boolean> = selectWith('containerCount')
export const selectWithTags: Selector<boolean> = selectWith('tags')
export const selectWithExceptions: Selector<boolean> = selectWith('exceptions')
export const selectWithNotes: Selector<boolean> = selectWith('notes')
export const selectWithPurchaseOrder: Selector<boolean> = selectWith('purchaseOrder')
export const selectWithDisposalTicket: Selector<boolean> = selectWith('disposalTicket')
export const selectWithImages: Selector<boolean> = selectWith('images')
export const selectWithVideo: Selector<boolean> = selectWith('video')
export const selectWithOverflowMenu: Selector<boolean> = selectWith('overflowMenu')

export const selectCardAttributeVisibility: Selector<Record<BoardV2CardToggleKey, boolean>> = selectVisibility

export default persistentBoardV2CardSlice.reducer
